'use client';

import { ReactNode, useCallback, useRef, useState } from 'react';

import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

const TRACK_WIDTH = 80;
const THUMB_WIDTH = 40;
const MAX_OFFSET = TRACK_WIDTH - THUMB_WIDTH;

interface CarouselScrollProps {
  children: ReactNode;
  gradient?: boolean;
  progress?: number;
}

export const useCarouselScroll = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;
    setScrollProgress(el.scrollLeft / maxScroll);
  }, []);

  const handleProgressChange = useCallback((progress: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollLeft = progress * maxScroll;
  }, []);

  return { scrollRef, scrollProgress, handleScroll, handleProgressChange };
};

export const CarouselScrollContainer = ({ children, gradient = true, progress = 0 }: CarouselScrollProps) => {
  const gradientOpacity = progress >= 1 ? 0 : progress > 0.9 ? (1 - progress) / 0.1 : 1;

  return (
    <div className={css({ position: 'relative' })}>
      {children}
      {gradient && (
        <div
          className={css({
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            w: 80,
            pointerEvents: 'none',
            background: 'linear-gradient(to right, transparent, token(colors.background.default.level-0))',
            tabletDown: { display: 'none' },
            transition: 'opacity 0.2s ease',
          })}
          style={{ opacity: gradientOpacity }}
        />
      )}
    </div>
  );
};

export const scrollTrackStyles = css.raw({
  display: 'flex',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

interface ScrollIndicatorProps {
  progress: number;
  onProgressChange: (progress: number) => void;
}

export const ScrollIndicator = ({ progress, onProgressChange }: ScrollIndicatorProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const calcProgress = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const x = clientX - rect.left - THUMB_WIDTH / 2;
      const clamped = Math.max(0, Math.min(x / MAX_OFFSET, 1));
      onProgressChange(clamped);
    },
    [onProgressChange],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      draggingRef.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      calcProgress(e.clientX);
    },
    [calcProgress],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return;
      calcProgress(e.clientX);
    },
    [calcProgress],
  );

  const handlePointerUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  return (
    <div className={center()}>
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={css({
          w: TRACK_WIDTH,
          h: 12,
          py: 4,
          rounded: 1000,
          cursor: 'pointer',
          position: 'relative',
          touchAction: 'none',
        })}
      >
        <div className={css({ w: '100%', h: 4, rounded: 1000, bg: 'background.default.level-3' })} />
        <div
          className={css({
            position: 'absolute',
            top: 4,
            left: 0,
            w: THUMB_WIDTH,
            h: 4,
            rounded: 1000,
            bg: 'content.default.level-5',
          })}
          style={{ transform: `translateX(${progress * MAX_OFFSET}px)` }}
        />
      </div>
    </div>
  );
};
